import { useState, useEffect, useCallback } from 'react'
import { RadioGroup, RadioGroupItem } from '@shopflo/ui'
import CheckboxAccordion from 'src/components/common/checkbox-accordion/CheckboxAccordion'
import { CheckboxItem } from 'src/components/common/checkbox-accordion/CheckboxList'
import { ExportTypeValues, ExportConfigStep1, Column, ExportType } from 'src/components/common/export-component/types'
import { SkeletonAccordion } from './Skeleton'
import { useRouter } from 'next/router'
import { useLocale } from 'src/lib/hooks/useLocal'

interface Step1ContentProps {
  isLoading: boolean
  columns: Column[]
  config: ExportConfigStep1
  onChange: (config: ExportConfigStep1) => void
  onValidationChange: (isValid: boolean) => void
}

const Step1Content = ({ columns, isLoading, config, onChange, onValidationChange }: Step1ContentProps) => {
  const router = useRouter()
  const { t } = useLocale()
  const routePath =
    router.pathname === '/orders'
      ? 'orders'
      : router.pathname === '/abandoned-checkouts'
      ? 'abandoned_checkouts'
      : router.pathname === '/download/report'
      ? 'report'
      : 'orders'

  const [error, setError] = useState<string>('')

  // Function to check if a column or any of its subcolumns are selected
  const isColumnOrSubcolumnsSelected = (column: Column): boolean => {
    if (config.selectedColumns.includes(column.name)) return true
    if (column.subcolumns) {
      return column.subcolumns.some(subcolumn => isColumnOrSubcolumnsSelected(subcolumn))
    }
    return false
  }

  // Function to validate selection
  const validateSelection = useCallback(() => {
    const hasSelection = columns?.some(column => isColumnOrSubcolumnsSelected(column))
    if (!hasSelection) {
      setError('Please select at least one column to export')
      onValidationChange(false)
    } else {
      setError('')
      onValidationChange(true)
    }
  }, [columns, config.selectedColumns, onValidationChange])

  // Set initial validation to true on mount
  useEffect(() => {
    onValidationChange(true)
  }, [])

  // Update validation whenever selection changes
  useEffect(() => {
    if (config.exportType !== 'today') {
      validateSelection()
    } else {
      onValidationChange(true)
    }
  }, [config.selectedColumns, validateSelection])

  const renderRadioOptions = (routePath: string) => {
    switch (routePath) {
      case 'orders':
        return (
          <>
            <RadioGroupItem value='orders' id='export-orders' label='Export orders' />
            <RadioGroupItem value='refunds' id='export-refunds' label='Export refunds' />
            <RadioGroupItem value='today' id='export-today' label="Export today's orders" />
          </>
        )
      case 'abandoned_checkouts':
        return (
          <>
            <RadioGroupItem value='abandoned_checkouts' id='export-abandoned' label='Abandoned checkouts' />
            <RadioGroupItem value='all_checkouts' id='export-all-checkouts' label='All checkouts' />
            <RadioGroupItem value='today' id='export-today-checkouts' label='All checkouts for Today' />
          </>
        )
      case 'report':
      default:
        return <></>
    }
  }
  const handleExportTypeChange = (value: ExportType) => {
    onChange({
      ...config,
      exportType: value,
      selectedColumns: []
    })
  }

  // Transform columns to match CheckboxAccordion item structure
  const transformColumns = (columns: Column[]): CheckboxItem[] => {
    return columns?.map((column: Column) => ({
      id: column.name,
      name: column.title,
      checked: config.selectedColumns.includes(column.name),
      children: column.subcolumns ? transformColumns(column.subcolumns) : undefined,
      count: column.subcolumns ? column.subcolumns.length : undefined,
      description: column.description ? column.description : undefined
    }))
  }

  const getAllSelectedIds = (checkBoxData: readonly CheckboxItem[]): string[] => {
    const selectedIds: string[] = []

    checkBoxData.forEach(item => {
      if (item.checked) {
        selectedIds.push(item.id.toString())
      }
      if (item.children) {
        selectedIds.push(...getAllSelectedIds(item.children))
      }
    })

    return selectedIds
  }

  return (
    <>
      {routePath !== 'report' && (
        <>
          <h2 className='text-text-md-medium mt-2'>{t('export_options')}</h2>
          <div className='mt-3'>
            <RadioGroup
              value={config.exportType}
              onValueChange={(value: string) => {
                if (ExportTypeValues.includes(value as ExportType)) {
                  handleExportTypeChange(value as ExportType)
                }
              }}
              className='flex flex-col gap-3'
            >
              {renderRadioOptions(routePath)}
            </RadioGroup>
          </div>
        </>
      )}

      {config.exportType !== 'today' && (
        <div className='w-full'>
          {routePath !== 'report' && <h2 className='text-text-md-medium mt-6'>{t('select_columns_to_export')}</h2>}
          {isLoading ? (
            <SkeletonAccordion />
          ) : (
            <div className='max-h-[320px] overflow-auto mt-2'>
              <CheckboxAccordion
                defaultCheckboxData={transformColumns(columns)}
                onChange={checkBoxData => {
                  const selectedColumns = getAllSelectedIds(checkBoxData)
                  onChange({
                    ...config,
                    selectedColumns: selectedColumns
                  })
                }}
              />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default Step1Content
